import { devplus } from './modules/common.js';

const airtable_proxy_url = 'https://airtable-api.rendervault.io/list';
const airtable_count_url = 'https://airtable-api.rendervault.io/count';
const airtable_fields = [ 'Collection', 'Colorref', 'Colorname', 'Texture', 'Intensity' ];
const airtable_id_fields = [ 'Collection', 'Colorref', 'Colorname', 'Texture', 'Intensity' ];
const optional_airtable_id_fields = new Set( [ 'Intensity' ] );
const required_fields_filter = 'AND({Fases}="Fase 1",{Colorref},{Colorname},{Texture},{Collection})';
const page_size = 100;
const search_delay = 250;
const start_render_button_label = 'Render starten';
const validating_render_button_label = 'SKU\'s valideren...';
const warming_up_worker_button_label = 'Worker zoeken en opwarmen...';
const requesting_render_button_label = 'Render aanvragen...';
const creating_zip_button_label = 'ZIP voorbereiden...';
const render_error_message = 'Er ging iets mis bij het valideren van de geselecteerde SKU\'s. Probeer het later opnieuw.';
const render_start_error_message = 'Het renderen kon niet worden gestart. Probeer het later opnieuw.';
const warmup_cancelled_error_message = 'Het opwarm proces werd geannuleerd. Probeer opnieuw.';
const warmup_failed_error_message = 'Er kon geen worker worden opgewarmd. Probeer opnieuw.';
const zip_error_message = 'De renders zijn klaar, maar de zip kon niet worden aangemaakt. Probeer het later opnieuw.';
const runpod_queue_url = 'https://runpod-queue.rendervault.io';
const runpod_owner = 'stoopen-meeus';
const runpod_project = 'stoopen-meeus';
const render_setting_placeholder = 'Selecteer een render setting...';
const render_setting_required_message = 'Selecteer eerst een render setting om de render te starten.';
const render_settings = [
	{
		value: 'topview',
		label: 'Topview',
		scene: 'scene_1.blend'
	},
	{
		value: 'topview_120_60',
		label: 'Topview (120x60)',
		scene: 'scene_1_remix.blend'
	},
	{
		value: 'interior_wide',
		label: 'Interieur Wijd',
		scene: 'scene_2.blend',
		camera: 'wide'
	},
	{
		value: 'interior_wide_120_60',
		label: 'Interieur Wijd (120x60)',
		scene: 'scene_2_remix.blend',
		camera: 'wide'
	},
	{
		value: 'interior_side',
		label: 'Interieur Zijkant',
		scene: 'scene_2.blend',
		camera: 'side'
	},
	{
		value: 'interior_side_120_60',
		label: 'Interieur Zijkant (120x60)',
		scene: 'scene_2_remix.blend',
		camera: 'side'
	}
];
const render_status_poll_delay = 2000;
const zip_status_poll_delay = 2000;
const run_stop_statuses = [ 'COMPLETED', 'FAILED', 'CANCELLED' ];

const state = {
	items: [],
	total_count: null,
	next_offset: null,
	has_more: true,
	is_fetching: false,
	search_term: '',
	active_query: '',
	selected_ids: new Set(),
	is_validating_render: false,
	render_button_label: validating_render_button_label,
	render_error: '',
	render_job_id: '',
	selected_render_setting: '',
	collapsed_collections: new Set(),
	loaded_record_ids: new Set(),
	request_key: 0,
	search_timeout: null,
	observer: null,
	load_error: '',
	elements: {}
};

const dom_is_ready = ( callback ) => {

	if ( document.readyState !== 'loading' ) {

		callback();

	} else {

		document.addEventListener( 'DOMContentLoaded', callback );

	}

};

dom_is_ready( async () => {

	const has_data = await get_data();

	if ( ! has_data ) {

		show_loader_error();
		return;

	}

	build_interface();
	render();

} );

async function get_data() {

	const loader = document.createElement( 'div' );

	loader.innerHTML = 'Data laden...';
	loader.classList.add( 'loader' );

	devplus.wrapper.replaceChildren( loader );
	state.elements.loader = loader;

	loader.offsetHeight;
	loader.classList.add( 'fade-in' );

	const total_count = await fetch_count();

	if ( typeof total_count === 'number' ) {

		state.total_count = total_count;

	}

	const has_results = await refresh_results( { query: '' } );

	if ( ! has_results ) {

		return false;

	}

	return true;

}

async function fetch_data( { offset = '', search_term = '' } = {} ) {

	const filter_by_formula = build_filter_formula( search_term );

	const params = new URLSearchParams( {
		project: 'stoopen-meeus',
		table: 'Table 1',
		view: 'Database',
		filterByFormula: filter_by_formula,
		pageSize: String( page_size )
	} );

	for ( const field of airtable_fields ) {

		params.append( 'fields', field );

	}

	if ( offset ) {

		params.set( 'offset', offset );

	}

	const url = `${airtable_proxy_url}?${params.toString()}`;

	try {

		const response = await fetch( url );
		const data = await response.json();

		if ( ! response.ok ) {

			return {
				error: extract_error_message( data )
			};

		}

		return data;

	} catch ( error ) {

		console.error( `Error fetching data from ${url}:`, error );
		return false;

	}

}

async function fetch_count() {

	const params = new URLSearchParams( {
		project: 'stoopen-meeus',
		table: 'Table 1',
		view: 'Database',
		filterByFormula: required_fields_filter
	} );

	const url = `${airtable_count_url}?${params.toString()}`;

	try {

		const response = await fetch( url );

		if ( ! response.ok ) {

			return null;

		}

		const data = await response.json();

		if ( typeof data?.total !== 'number' ) {

			return null;

		}

		return data.total;

	} catch ( error ) {

		console.error( `Error fetching data from ${url}:`, error );
		return null;

	}

}

function build_interface() {

	const app = document.createElement( 'section' );
	app.className = 'sku-browser';

	app.innerHTML = `
		<div class="sku-browser-alert" role="alert" hidden>
			<p></p>
			<button class="sku-browser-alert-close" type="button" aria-label="Melding sluiten">
				<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
					<path d="M18 6 6 18"></path>
					<path d="m6 6 12 12"></path>
				</svg>
			</button>
		</div>
		<header class="sku-browser-header">
			<div class="sku-browser-heading">
				<h1 class="sku-browser-title">SKU's</h1>
			</div>
			<div class="sku-browser-status">
				<span class="sku-browser-header-loader" aria-hidden="true"></span>
				<p class="sku-browser-meta"></p>
			</div>
		</header>
		<div class="sku-browser-list" aria-live="polite">
			<div class="sku-browser-toolbar">
				<form class="sku-browser-search" novalidate>
					<button class="sku-browser-search-button" type="submit" aria-label="Zoeken"></button>
					<label class="sku-browser-search-label" aria-label="Zoek SKU's">
						<input type="search" name="search" placeholder="Zoek SKU's" autocomplete="off" />
					</label>
				</form>
			</div>
			<div class="sku-browser-groups"></div>
		</div>
		<div class="sku-browser-actions">
			<label class="render-setting-field">
				<span class="render-setting-label">Render setting</span>
				<span class="render-setting-select-wrap">
					<select class="render-setting-select" name="render_setting" required>
						<option value="">${render_setting_placeholder}</option>
						${build_render_setting_options()}
					</select>
				</span>
			</label>
			<button class="start-render-btn" type="button" disabled>
				<svg class="start-render-btn-loader" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
					<path opacity="0.35" d="M7 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Zm0 1.5a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z"></path>
					<path d="M7 1.5A5.5 5.5 0 0 1 12.5 7H11a4 4 0 0 0-4-4V1.5Z"></path>
				</svg>
				<svg class="start-render-btn-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
					<polygon points="6 3 20 12 6 21 6 3"></polygon>
				</svg>
				<span>${start_render_button_label}</span>
			</button>
		</div>
	`;

	devplus.wrapper.replaceChildren( app );

	state.elements = {
		app,
		search: app.querySelector( 'input[name="search"]' ),
		search_form: app.querySelector( '.sku-browser-search' ),
		header_loader: app.querySelector( '.sku-browser-header-loader' ),
		meta: app.querySelector( '.sku-browser-meta' ),
		list: app.querySelector( '.sku-browser-list' ),
		groups: app.querySelector( '.sku-browser-groups' ),
		render_alert: app.querySelector( '.sku-browser-alert' ),
		render_alert_message: app.querySelector( '.sku-browser-alert p' ),
		render_alert_close: app.querySelector( '.sku-browser-alert-close' ),
		render_setting_select: app.querySelector( 'select[name="render_setting"]' ),
		start_render_button: app.querySelector( '.start-render-btn' ),
		start_render_button_label: app.querySelector( '.start-render-btn span' )
	};

	state.elements.search.addEventListener( 'input', handle_search_input );
	state.elements.search_form.addEventListener( 'submit', handle_search_submit );
	state.elements.groups.addEventListener( 'change', handle_group_change );
	state.elements.groups.addEventListener( 'click', handle_group_click );
	state.elements.render_setting_select.addEventListener( 'change', handle_render_setting_change );
	state.elements.start_render_button.addEventListener( 'click', handle_start_render_click );
	state.elements.render_alert_close.addEventListener( 'click', clear_render_error );

}

function build_render_setting_options() {

	return render_settings.map( ( setting ) => (
		`<option value="${escape_attribute( setting.value )}">${escape_html( setting.label )}</option>`
	) ).join( '' );

}

function render() {

	if ( ! is_interface_ready() ) {

		return;

	}

	const grouped_items = group_items( state.items );

	render_summary( state.items );
	render_groups( grouped_items );

}

function render_summary( visible_items ) {

	const total_visible = visible_items.length;
	const selected_visible_count = visible_items.filter( ( item ) => state.selected_ids.has( get_item_data_id( item ) ) ).length;
	const meta = state.elements.meta;

	meta.textContent = build_summary_meta( selected_visible_count );
	sync_header_loading();
	sync_start_render_button();

}

function render_groups( grouped_items ) {

	const fragment = document.createDocumentFragment();
	const total_visible = grouped_items.reduce( ( total, [ , items ] ) => total + items.length, 0 );
	const selected_visible_count = state.items.filter( ( item ) => state.selected_ids.has( get_item_data_id( item ) ) ).length;
	const all_visible_selected = total_visible > 0 && selected_visible_count === total_visible;

	if ( grouped_items.length === 0 ) {

		const empty = document.createElement( 'p' );
		empty.className = 'sku-browser-empty';
		empty.textContent = state.is_fetching && state.active_query
			? 'Items aan het laden...'
			: state.search_term
				? 'Geen items gevonden voor deze zoekterm.'
				: 'Geen items beschikbaar.';
		fragment.appendChild( empty );

		state.elements.groups.replaceChildren( fragment );
		sync_selection_controls();
		return;

	}

	const summary_group = document.createElement( 'section' );
	summary_group.className = 'sku-group sku-group-summary';
	summary_group.innerHTML = `
		<header class="sku-group-header">
			<div class="sku-group-heading">
				<label class="sku-browser-checkbox sku-browser-checkbox-summary">
					<input type="checkbox" data-role="toggle_all" ${all_visible_selected ? 'checked' : ''} />
					<span>Alle items</span>
				</label>
			</div>
			<span class="sku-group-count">${selected_visible_count}/${total_visible}</span>
		</header>
	`;

	const summary_checkbox = summary_group.querySelector( '[data-role="toggle_all"]' );
	summary_checkbox.indeterminate = selected_visible_count > 0 && ! all_visible_selected;
	fragment.appendChild( summary_group );

	for ( const [ collection_name, items ] of grouped_items ) {

		const group = document.createElement( 'section' );
		group.className = 'sku-group';
		group.dataset.collection = collection_name;

		const selected_count = items.filter( ( item ) => state.selected_ids.has( get_item_data_id( item ) ) ).length;
		const all_selected = items.length > 0 && selected_count === items.length;
		const is_collapsed = state.collapsed_collections.has( collection_name );
		const item_markup = items.map( ( item ) => `
			<label class="sku-browser-checkbox sku-browser-checkbox-item">
				<input type="checkbox" data-role="toggle_item" data-id="${escape_attribute( get_item_data_id( item ) )}" ${state.selected_ids.has( get_item_data_id( item ) ) ? 'checked' : ''} />
				<span>${escape_html( item.name )}</span>
			</label>
		` ).join( '' );

		group.innerHTML = `
			<header class="sku-group-header">
				<div class="sku-group-heading">
					<button class="sku-group-toggle" type="button" data-role="toggle_collection" data-collection="${escape_attribute( collection_name )}" aria-expanded="${is_collapsed ? 'false' : 'true'}" aria-label="${is_collapsed ? 'Open groep' : 'Sluit groep'}"></button>
					<label class="sku-browser-checkbox sku-browser-checkbox-group">
						<input type="checkbox" data-role="toggle_group" data-collection="${escape_attribute( collection_name )}" ${all_selected ? 'checked' : ''} />
						<span>${escape_html( collection_name )}</span>
					</label>
				</div>
				<span class="sku-group-count">${selected_count}/${items.length}</span>
			</header>
			<div class="sku-group-items${is_collapsed ? ' is-collapsed' : ''}">${item_markup}</div>
		`;

		const group_checkbox = group.querySelector( '[data-role="toggle_group"]' );
		group_checkbox.indeterminate = selected_count > 0 && ! all_selected;

		fragment.appendChild( group );

	}

	state.elements.groups.replaceChildren( fragment );
	sync_selection_controls();

}

function handle_group_click( event ) {

	const toggle_button = event.target instanceof Element
		? event.target.closest( '[data-role="toggle_collection"]' )
		: null;

	if ( ! toggle_button ) {

		return;

	}

	const collection = toggle_button.getAttribute( 'data-collection' ) || '';

	if ( ! collection ) {

		return;

	}

	if ( state.collapsed_collections.has( collection ) ) {

		state.collapsed_collections.delete( collection );

	} else {

		state.collapsed_collections.add( collection );

	}

	render();

}

function build_summary_meta( selected_visible_count ) {

	const total_count_label = typeof state.total_count === 'number'
		? `${state.total_count} SKU's in totaal`
		: 'totaal onbekend';

	if ( state.active_query ) {

		return `${selected_visible_count} geselecteerd, ${state.items.length} zoekresultaten`;

	}

	return `${selected_visible_count} geselecteerd, ${total_count_label}`;

}

function handle_search_input( event ) {

	state.search_term = String( event.target.value || '' ).trim();

	window.clearTimeout( state.search_timeout );

	state.search_timeout = window.setTimeout( () => {

		if ( ! state.search_term ) {

			if ( state.active_query ) {

				void refresh_results( { query: '' } );

			}

			return;

		}

		void refresh_results( { query: state.search_term } );

	}, search_delay );

}

function handle_search_submit( event ) {

	event.preventDefault();

	state.search_term = String( state.elements.search.value || '' ).trim();

	window.clearTimeout( state.search_timeout );

	if ( ! state.search_term ) {

		if ( state.active_query ) {

			void refresh_results( { query: '' } );

		}

		return;

	}

	void refresh_results( { query: state.search_term } );

}

function handle_render_setting_change( event ) {

	state.selected_render_setting = String( event.target.value || '' );

	if ( get_selected_render_setting() ) {

		clear_render_error();

	}

	sync_start_render_button();

}

function handle_toggle_all( event ) {

	for ( const item of state.items ) {

		if ( event.target.checked ) {

			state.selected_ids.add( get_item_data_id( item ) );

		} else {

			state.selected_ids.delete( get_item_data_id( item ) );

		}

	}

	render();

}

function handle_group_change( event ) {

	if ( state.is_validating_render ) {

		return;

	}

	const { target } = event;

	if ( ! ( target instanceof HTMLInputElement ) ) {

		return;

	}

	const role = target.getAttribute( 'data-role' );

	if ( role === 'toggle_all' ) {

		handle_toggle_all( event );
		return;

	}

	if ( role === 'toggle_group' ) {

		const collection = target.getAttribute( 'data-collection' ) || '';
		const items = state.items.filter( ( item ) => item.collection === collection );

		for ( const item of items ) {

			if ( target.checked ) {

				state.selected_ids.add( get_item_data_id( item ) );

			} else {

				state.selected_ids.delete( get_item_data_id( item ) );

			}

		}

		render();
		return;

	}

	if ( role === 'toggle_item' ) {

		const item_id = target.getAttribute( 'data-id' );

		if ( ! item_id ) {

			return;

		}

		if ( target.checked ) {

			state.selected_ids.add( item_id );

		} else {

			state.selected_ids.delete( item_id );

		}

		render();

	}

}

async function handle_start_render_click() {

	if ( state.is_validating_render || state.selected_ids.size === 0 ) {

		return;

	}

	const render_setting = get_selected_render_setting();

	if ( ! render_setting ) {

		show_render_error( render_setting_required_message );
		sync_start_render_button();
		state.elements.render_setting_select?.focus();
		return;

	}

	clear_render_error();

	const selected_item_ids = [ ...state.selected_ids ];
	const cache_version = create_render_cache_version();

	state.is_validating_render = true;
	state.render_job_id = '';
	set_render_button_label( validating_render_button_label );

	try {

		const response = await post_selected_item_ids( selected_item_ids );
		const response_text = await response.text();

		console.log( 'Render validation response:', response_text );

		if ( ! response.ok ) {

			console.error( 'Render validation failed:', response_text );
			show_render_error( build_render_error_message( response_text ) );
			return;

		}

		state.render_job_id = extract_render_job_id( response_text );

		if ( ! state.render_job_id ) {

			throw create_render_flow_error( render_start_error_message, 'Render validation succeeded without a job id.' );

		}

		console.log( 'Render job id:', state.render_job_id );

		set_render_button_label( warming_up_worker_button_label );
		await warmup_runpod_worker();

		set_render_button_label( requesting_render_button_label );
		await render_selected_items( state.render_job_id, selected_item_ids, render_setting, cache_version );

		set_render_button_label( creating_zip_button_label );

		const zip_data = await create_render_zip( state.render_job_id );

		console.log( 'Render zip ready:', zip_data );
		trigger_render_zip_download( zip_data.zip_url, zip_data.zip_filename );

	} catch ( error ) {

		if ( error?.debug_message ) {

			console.error( 'Render failed:', error.debug_message, error );

		} else {

			console.error( 'Render failed:', error );

		}

		show_render_error( error?.user_message || render_error_message );

		if ( state.render_job_id && ! error?.skip_cleanup ) {

			await cleanup_render_job( state.render_job_id );

		}

	} finally {

		state.is_validating_render = false;
		state.render_button_label = validating_render_button_label;
		sync_start_render_button();

	}

}

async function render_selected_items( job_id, item_ids, render_setting, cache_version ) {

	for ( let index = 0; index < item_ids.length; index++ ) {

		const item_id = item_ids[ index ];
		const sku_index = index + 1;
		const total_skus = item_ids.length;

		set_render_progress_label( sku_index, total_skus, 'render aanvragen...' );

		const run_id = await request_render_run( job_id, item_id, sku_index, total_skus, render_setting, cache_version );

		await wait_for_render_completion( run_id, item_id, sku_index, total_skus );

	}

}

async function warmup_runpod_worker() {

	const run_id = await request_warmup_run();

	await wait_for_warmup_completion( run_id );

}

async function request_warmup_run() {

	let response;

	try {

		response = await fetch( `${runpod_queue_url}/run`, {
			method: 'POST',
			headers: build_runpod_headers(),
			body: JSON.stringify( {
				warmup: true
			} )
		} );

	} catch ( error ) {

		throw create_render_flow_error( warmup_failed_error_message, `Worker warmup request failed. ${error.message || error}` );

	}

	if ( ! response.ok ) {

		const error_message = await response.text().catch( () => '' );

		throw create_render_flow_error( warmup_failed_error_message, `Worker warmup request failed. ${response.status} ${error_message}` );

	}

	let run_json;

	try {

		run_json = await response.json();

	} catch ( error ) {

		throw create_render_flow_error( warmup_failed_error_message, `Worker warmup request returned invalid JSON. ${error.message || error}` );

	}

	if ( ! run_json?.id ) {

		throw create_render_flow_error( warmup_failed_error_message, 'Worker warmup request did not return a run id.' );

	}

	return run_json.id;

}

async function wait_for_warmup_completion( run_id ) {

	let run_status = false;

	while ( ! run_stop_statuses.includes( run_status ) ) {

		let status_response;

		try {

			status_response = await fetch( `${runpod_queue_url}/status/${run_id}`, {
				method: 'POST',
				headers: build_runpod_headers()
			} );

		} catch ( error ) {

			const debug_message = `Worker warmup status request failed (${run_id}). ${error.message || error}`;
			console.error( debug_message );
			throw create_render_flow_error( warmup_failed_error_message, debug_message );

		}

		if ( ! status_response.ok ) {

			const error_message = await status_response.text().catch( () => '' );
			const debug_message = `Worker warmup status request failed (${run_id}). ${status_response.status} ${error_message}`;

			console.error( debug_message );
			throw create_render_flow_error( warmup_failed_error_message, debug_message );

		}

		let status_json;

		try {

			status_json = await status_response.json();

		} catch ( error ) {

			const debug_message = `Worker warmup status returned invalid JSON (${run_id}). ${error.message || error}`;
			console.error( debug_message );
			throw create_render_flow_error( warmup_failed_error_message, debug_message );

		}

		if ( ! status_json?.status ) {

			const debug_message = `Worker warmup status response had no status (${run_id}).`;
			console.error( debug_message, status_json );
			throw create_render_flow_error( warmup_failed_error_message, debug_message );

		}

		run_status = status_json.status;

		if ( run_status === 'COMPLETED' ) {

			return;

		} else if ( run_status === 'CANCELLED' ) {

			const debug_message = `Worker warmup cancelled (${run_id}).`;
			console.error( debug_message, status_json );
			throw create_render_flow_error( warmup_cancelled_error_message, debug_message );

		} else if ( run_status === 'FAILED' ) {

			const debug_message = `Worker warmup failed (${run_id}).`;
			console.error( debug_message, status_json );
			throw create_render_flow_error( warmup_failed_error_message, debug_message );

		}

		await delay( render_status_poll_delay );

	}

}

async function request_render_run( job_id, item_id, sku_index, total_skus, render_setting, cache_version ) {

	let response;
	const queue_item_id = item_id.replaceAll( '##', '-' );
	const render_payload = build_render_run_payload( job_id, item_id, queue_item_id, render_setting, cache_version );

	try {

		response = await fetch( `${runpod_queue_url}/run`, {
			method: 'POST',
			headers: build_runpod_headers(),
			body: JSON.stringify( render_payload )
		} );

	} catch ( error ) {

		throw create_render_flow_error( render_start_error_message, `Render request failed for SKU ${sku_index}/${total_skus} (${item_id}). ${error.message || error}` );

	}

	if ( ! response.ok ) {

		const error_message = await response.text().catch( () => '' );

		throw create_render_flow_error( render_start_error_message, `Render request failed for SKU ${sku_index}/${total_skus} (${item_id}). ${response.status} ${error_message}` );

	}

	let run_json;

	try {

		run_json = await response.json();

	} catch ( error ) {

		throw create_render_flow_error( render_start_error_message, `Render request returned invalid JSON for SKU ${sku_index}/${total_skus} (${item_id}). ${error.message || error}` );

	}

	if ( ! run_json?.id ) {

		throw create_render_flow_error( render_start_error_message, `Render request did not return a run id for SKU ${sku_index}/${total_skus} (${item_id}).` );

	}

	return run_json.id;

}

async function wait_for_render_completion( run_id, item_id, sku_index, total_skus ) {

	let run_status = false;
	let run_percent_set = false;

	while ( ! run_stop_statuses.includes( run_status ) ) {

		let status_response;

		try {

			status_response = await fetch( `${runpod_queue_url}/status/${run_id}`, {
				method: 'POST',
				headers: build_runpod_headers()
			} );

		} catch ( error ) {

			const debug_message = `Render status request failed for SKU ${sku_index}/${total_skus} (${item_id}). ${error.message || error}`;
			console.error( debug_message );
			throw create_render_flow_error( build_render_progress_error_message( sku_index, total_skus ), debug_message );

		}

		if ( ! status_response.ok ) {

			const error_message = await status_response.text().catch( () => '' );
			const debug_message = `Render status request failed for SKU ${sku_index}/${total_skus} (${item_id}). ${status_response.status} ${error_message}`;

			console.error( debug_message );
			throw create_render_flow_error( build_render_progress_error_message( sku_index, total_skus ), debug_message );

		}

		let status_json;

		try {

			status_json = await status_response.json();

		} catch ( error ) {

			const debug_message = `Render status returned invalid JSON for SKU ${sku_index}/${total_skus} (${item_id}). ${error.message || error}`;
			console.error( debug_message );
			throw create_render_flow_error( build_render_progress_error_message( sku_index, total_skus ), debug_message );

		}

		if ( ! status_json?.status ) {

			const debug_message = `Render status response had no status for SKU ${sku_index}/${total_skus} (${item_id}).`;
			console.error( debug_message, status_json );
			throw create_render_flow_error( build_render_progress_error_message( sku_index, total_skus ), debug_message );

		}

		run_status = status_json.status;

		if ( run_status === 'IN_PROGRESS' ) {

			set_render_progress_label( sku_index, total_skus, run_percent_set ? 'afronden...' : 'renderen...' );

			const output = typeof status_json.output === 'string' ? status_json.output : '';

			if ( output.includes( 'Time' ) && output.includes( 'Remaining' ) ) {

				const match = output.match( /Time:(\d{2}:\d{2}(?:\.\d+)?)\s*\|\s*Remaining:(\d{2}:\d{2}(?:\.\d+)?)/ );

				if ( match ) {

					const elapsed = parse_time_to_seconds( match[ 1 ] );
					const remaining = parse_time_to_seconds( match[ 2 ] );
					const total = elapsed + remaining;

					run_percent_set = true;

					const percent = total > 0 ? Math.ceil( ( elapsed / total ) * 100 ) : 0;

					if ( percent !== 0 ) {

						set_render_progress_label( sku_index, total_skus, `renderen (${percent}%)` );

					}

				}

			}

		} else if ( run_status === 'COMPLETED' ) {

			return;

		} else if ( run_status === 'FAILED' || run_status === 'CANCELLED' ) {

			const debug_message = `Render ${run_status.toLowerCase()} for SKU ${sku_index}/${total_skus} (${item_id}).`;
			console.error( debug_message, status_json );
			throw create_render_flow_error( build_render_progress_error_message( sku_index, total_skus ), debug_message );

		}

		await delay( render_status_poll_delay );

	}

}

function build_render_run_payload( job_id, item_id, queue_item_id, render_setting, cache_version ) {

	const payload = {
		job: job_id,
		project: runpod_project,
		scene: render_setting.scene,
		cache_version,
		id: queue_item_id,
		material_map: [
			{
				mesh: 'Plane',
				assets: build_render_assets( item_id )
			}
		]
	};

	if ( render_setting.camera ) {

		payload.camera = render_setting.camera;

	}

	return payload;

}

function create_render_cache_version() {

	const timestamp = new Date()
		.toISOString()
		.replace( /[-:.]/g, '' );
	const high_resolution_time = Math.round( performance.now() * 1000 ).toString( 36 );
	const random_part = create_cache_version_random_part();

	return `render-${timestamp}-${high_resolution_time}-${random_part}`;

}

function create_cache_version_random_part() {

	if ( globalThis.crypto?.randomUUID ) {

		return globalThis.crypto.randomUUID();

	}

	if ( globalThis.crypto?.getRandomValues ) {

		const values = new Uint32Array( 4 );
		globalThis.crypto.getRandomValues( values );

		return [ ...values ].map( ( value ) => value.toString( 36 ) ).join( '-' );

	}

	return `${Date.now().toString( 36 )}-${Math.random().toString( 36 ).slice( 2 )}`;

}

function build_render_assets( item_id ) {

	const structure_prefix = 'StoopenMeeus';
	const split = item_id.split( '##' );

	let color_asset_id = split.slice( 0, 3 ).join( '-' );

	if ( split.length === 5 ) {

		color_asset_id += `-${split[4]}`;

	}

	return {
		basecolor: `${structure_prefix}__${split[ 3 ]}__${color_asset_id}_Color.jpg`,
		roughness: `${structure_prefix}__${split[3]}_Roughness.jpg`,
		normal: `${structure_prefix}__${split[ 3 ]}_Normal.jpg`,
		heightmap: `${structure_prefix}__${split[ 3 ]}_Heightmap.jpg`
	};

}

function build_runpod_headers() {

	return {
		'Content-Type': 'application/json',
		'X-Runpod-Owner': runpod_owner
	};

}

function build_render_progress_error_message( sku_index, total_skus ) {

	return `Het renderen voor SKU ${sku_index}/${total_skus} kon niet worden afgewerkt. Probeer het later opnieuw.`;

}

function create_render_flow_error( user_message, debug_message, options = {} ) {

	const error = new Error( debug_message || user_message );

	error.user_message = user_message;
	error.debug_message = debug_message || user_message;
	error.skip_cleanup = Boolean( options.skip_cleanup );

	return error;

}

function delay( ms ) {

	return new Promise( ( resolve ) => window.setTimeout( resolve, ms ) );

}

function parse_time_to_seconds( mmss ) {

	const [ minutes, seconds ] = String( mmss || '0:0' ).split( ':' );

	return ( Number( minutes ) * 60 ) + Number( seconds );

}

function extract_render_job_id( response_text ) {

	try {

		const data = JSON.parse( response_text );

		return typeof data?.job_id === 'string' ? data.job_id : '';

	} catch ( error ) {

		return '';

	}

}

function build_render_error_message( response_text ) {

	const item_id = extract_missing_asset_item_id( response_text );

	if ( item_id ) {

		return `SKU "${item_id.replaceAll( '##', '-' )}" heeft niet alle assets op de server staan om de render te starten. Controleer even en probeer hierna opnieuw.`;

	}

	return render_error_message;

}

function extract_missing_asset_item_id( response_text ) {

	const match = String( response_text || '' ).match( /\/([^/]+)_(?:color|normal|displacement|heightmap|roughness)\.jpg\b/i );

	return match ? match[ 1 ] : '';

}

function show_render_error( message ) {

	state.render_error = message;

	if ( ! state.elements.render_alert || ! state.elements.render_alert_message ) {

		return;

	}

	state.elements.render_alert_message.textContent = message;
	state.elements.render_alert.hidden = false;

}

function clear_render_error() {

	state.render_error = '';

	if ( ! state.elements.render_alert || ! state.elements.render_alert_message ) {

		return;

	}

	state.elements.render_alert_message.textContent = '';
	state.elements.render_alert.hidden = true;

}

function post_selected_item_ids( item_ids ) {

	const form_data = new FormData();

	form_data.append( 'action', 'validate_render_job' );

	for ( const item_id of item_ids ) {

		form_data.append( 'item_ids[]', item_id );

	}

	return fetch( '/', {
		method: 'POST',
		body: form_data,
		headers: {
			'X-Requested-With': 'XMLHttpRequest'
		}
	} );

}

async function create_render_zip( job_id ) {

	const zip_data = await request_render_zip( job_id );

	return wait_for_render_zip( job_id, zip_data );

}

async function request_render_zip( job_id ) {

	let response;

	try {

		response = await fetch( `${runpod_queue_url}/download-zip`, {
			method: 'POST',
			headers: {
				...build_runpod_headers()
			},
			body: JSON.stringify( {
				job_id: job_id,
				project: runpod_project
			} )
		} );

	} catch ( error ) {

		throw create_render_flow_error( zip_error_message, `Render zip request failed for ${job_id}. ${error.message || error}`, {
			skip_cleanup: true
		} );

	}

	const zip_data = await read_render_zip_response_json( response, job_id, 'request' );

	if ( ! response.ok && response.status !== 202 ) {

		throw create_render_flow_error( zip_error_message, `Render zip request failed for ${job_id}. ${response.status} ${get_render_zip_response_message( zip_data )}`, {
			skip_cleanup: true
		} );

	}

	return zip_data;

}

async function wait_for_render_zip( job_id, zip_data ) {

	const ready_zip_data = get_ready_render_zip_data( job_id, zip_data );

	if ( ready_zip_data ) {

		return ready_zip_data;

	}

	if ( zip_data?.status !== 'processing' ) {

		throw create_render_flow_error( zip_error_message, `Render zip response returned unexpected status for ${job_id}: ${get_render_zip_response_message( zip_data )}`, {
			skip_cleanup: true
		} );

	}

	if ( typeof zip_data.status_url !== 'string' || ! zip_data.status_url ) {

		throw create_render_flow_error( zip_error_message, `Render zip processing response had no status_url for ${job_id}.`, {
			skip_cleanup: true
		} );

	}

	const status_url = build_render_zip_status_url( zip_data.status_url );

	while ( true ) {

		await delay( zip_status_poll_delay );

		const status_data = await request_render_zip_status( job_id, status_url );
		const status_ready_zip_data = get_ready_render_zip_data( job_id, status_data );

		if ( status_ready_zip_data ) {

			return status_ready_zip_data;

		}

		if ( status_data?.status === 'error' || status_data?.status === 'missing' ) {

			throw create_render_flow_error( zip_error_message, `Render zip failed for ${job_id}. ${get_render_zip_response_message( status_data )}`, {
				skip_cleanup: true
			} );

		}

		if ( status_data?.status !== 'processing' ) {

			throw create_render_flow_error( zip_error_message, `Render zip status returned unexpected status for ${job_id}: ${get_render_zip_response_message( status_data )}`, {
				skip_cleanup: true
			} );

		}

	}

}

async function request_render_zip_status( job_id, status_url ) {

	let response;

	try {

		response = await fetch( status_url );

	} catch ( error ) {

		throw create_render_flow_error( zip_error_message, `Render zip status request failed for ${job_id}. ${error.message || error}`, {
			skip_cleanup: true
		} );

	}

	const status_data = await read_render_zip_response_json( response, job_id, 'status' );

	if ( ! response.ok ) {

		throw create_render_flow_error( zip_error_message, `Render zip status request failed for ${job_id}. ${response.status} ${get_render_zip_response_message( status_data )}`, {
			skip_cleanup: true
		} );

	}

	return status_data;

}

async function read_render_zip_response_json( response, job_id, type ) {

	try {

		return await response.json();

	} catch ( error ) {

		const context = type === 'status' ? 'status response' : 'response';

		throw create_render_flow_error( zip_error_message, `Render zip ${context} returned invalid JSON for ${job_id}. ${error.message || error}`, {
			skip_cleanup: true
		} );

	}

}

function get_ready_render_zip_data( job_id, zip_data ) {

	const status = typeof zip_data?.status === 'string' ? zip_data.status : '';

	if ( status !== '' && status !== 'ready' ) {

		return null;

	}

	if ( typeof zip_data?.url !== 'string' || ! zip_data.url ) {

		throw create_render_flow_error( zip_error_message, `Render zip response had no url for ${job_id}.`, {
			skip_cleanup: true
		} );

	}

	return {
		zip_url: zip_data.url,
		zip_filename: typeof zip_data.zip_filename === 'string' && zip_data.zip_filename
			? zip_data.zip_filename
			: get_filename_from_url( zip_data.url )
	};

}

function build_render_zip_status_url( status_url ) {

	return new URL( status_url, `${runpod_queue_url}/` ).href;

}

function get_render_zip_response_message( zip_data ) {

	if ( typeof zip_data?.message === 'string' && zip_data.message.trim() ) {

		return zip_data.message.trim();

	}

	if ( typeof zip_data?.error === 'string' && zip_data.error.trim() ) {

		return zip_data.error.trim();

	}

	try {

		return JSON.stringify( zip_data );

	} catch ( error ) {

		return 'unknown zip response';

	}

}

function get_filename_from_url( url ) {

	try {

		const pathname = new URL( url, window.location.href ).pathname;
		const filename = pathname.split( '/' ).filter( Boolean ).pop();

		return filename || '';

	} catch ( error ) {

		return '';

	}

}

function trigger_render_zip_download( zip_url, filename = '' ) {

	const link = document.createElement( 'a' );

	link.href = zip_url;
	link.download = filename || '';
	link.style.display = 'none';

	document.body.appendChild( link );
	link.click();
	link.remove();

}

async function cleanup_render_job( job_id ) {

	const form_data = new FormData();

	form_data.append( 'action', 'cleanup_render_job' );
	form_data.append( 'job_id', job_id );

	try {

		const response = await fetch( '/', {
			method: 'POST',
			body: form_data,
			headers: {
				'X-Requested-With': 'XMLHttpRequest'
			}
		} );
		const response_text = await response.text();

		if ( ! response.ok ) {

			console.error( 'Render job cleanup failed:', response_text );

		}

	} catch ( error ) {

		console.error( 'Render job cleanup failed:', error );

	}

}

function group_items( items ) {

	const groups = new Map();

	for ( const item of items ) {

		if ( ! groups.has( item.collection ) ) {

			groups.set( item.collection, [] );

		}

		groups.get( item.collection ).push( item );

	}

	return [ ...groups.entries() ];

}

async function refresh_results( { query = '' } = {} ) {

	const request_key = ++state.request_key;

	state.active_query = query;
	state.items = [];
	state.next_offset = null;
	state.has_more = true;
	state.loaded_record_ids = new Set();
	state.is_fetching = true;
	state.load_error = '';
	render();
	sync_header_loading();

	const page = await fetch_data( { search_term: query } );

	if ( request_key !== state.request_key ) {

		return false;

	}

	state.is_fetching = false;
	sync_header_loading();

	if ( ! page || ! Array.isArray( page.records ) ) {

		state.load_error = extract_error_message( page );
		return false;

	}

	ingest_page( page );
	render();

	await ensure_all_items_loaded();

	return true;

}

function is_interface_ready() {

	return Boolean( state.elements.meta && state.elements.groups && state.elements.list );

}

function get_item_data_id( item ) {

	return item.data_id || item.id;

}

function sync_header_loading() {

	if ( ! state.elements.header_loader ) {

		return;

	}

	state.elements.header_loader.classList.toggle( 'is-visible', state.is_fetching );

}

function sync_start_render_button() {

	if ( ! state.elements.start_render_button ) {

		return;

	}

	const is_validating = state.is_validating_render;
	const has_render_setting = Boolean( get_selected_render_setting() );

	state.elements.start_render_button.disabled = is_validating || state.selected_ids.size === 0 || ! has_render_setting;
	state.elements.start_render_button.classList.toggle( 'is-validating', is_validating );
	state.elements.start_render_button.setAttribute( 'aria-busy', is_validating ? 'true' : 'false' );

	if ( state.elements.render_setting_select ) {

		state.elements.render_setting_select.disabled = is_validating;

	}

	if ( state.elements.start_render_button_label ) {

		state.elements.start_render_button_label.textContent = is_validating
			? state.render_button_label
			: start_render_button_label;

	}

	sync_selection_controls();

}

function get_selected_render_setting() {

	return render_settings.find( ( setting ) => setting.value === state.selected_render_setting ) || null;

}

function sync_selection_controls() {

	if ( ! state.elements.groups ) {

		return;

	}

	const is_disabled = state.is_validating_render;

	state.elements.groups.classList.toggle( 'is-selection-disabled', is_disabled );

	for ( const checkbox of state.elements.groups.querySelectorAll( 'input[type="checkbox"]' ) ) {

		checkbox.disabled = is_disabled;

	}

}

function set_render_button_label( label ) {

	state.render_button_label = label;
	sync_start_render_button();

}

function set_render_progress_label( sku_index, total_skus, label ) {

	set_render_button_label( `SKU ${sku_index}/${total_skus}: ${label}` );

}

function ingest_page( page ) {

	const records = Array.isArray( page.records ) ? page.records : [];

	for ( const record of records ) {

		if ( ! record?.id || state.loaded_record_ids.has( record.id ) ) {

			continue;

		}

		state.loaded_record_ids.add( record.id );
		state.items.push( normalize_record( record ) );

	}

	state.next_offset = page.offset || null;
	state.has_more = Boolean( page.offset );

}

function normalize_record( record ) {

	const fields = record.fields || {};
	const collection = clean_value( fields.Collection ) || 'Zonder collectie';
	const data_id = build_airtable_fields_id( fields ) || record.id;
	const parts = [
		clean_value( fields.Colorref ),
		clean_value( fields.Colorname ),
		clean_value( fields.Texture ),
		clean_value( fields.Intensity )
	].filter( Boolean );
	const name = parts.join( ' - ' ) || record.id;

	return {
		id: record.id,
		data_id,
		collection,
		name
	};

}

async function fetch_next_page() {

	if ( state.is_fetching || ! state.has_more || ! state.next_offset ) {

		return false;

	}

	state.is_fetching = true;
	sync_header_loading();

	const page = await fetch_data( {
		offset: state.next_offset,
		search_term: state.active_query
	} );

	state.is_fetching = false;
	sync_header_loading();

	if ( ! page || ! Array.isArray( page.records ) ) {

		return false;

	}

	ingest_page( page );
	render();
	return true;

}

function show_loader_error() {

	if ( ! state.elements.loader ) {

		return;

	}

	state.elements.loader.textContent = state.load_error || 'De data kon niet geladen worden vanaf de Airtable proxy.';

}

async function ensure_all_items_loaded() {

	while ( state.has_more ) {

		const has_loaded_page = await fetch_next_page();

		if ( ! has_loaded_page ) {

			break;

		}

	}

}

function clean_value( value ) {

	if ( Array.isArray( value ) ) {

		return value.map( clean_value ).filter( Boolean ).join( ', ' );

	}

	return String( value || '' ).trim();

}

function build_airtable_fields_id( fields ) {

	const parts = [];

	for ( const field_name of airtable_id_fields ) {

		const part = clean_data_id_part( fields[ field_name ] );

		if ( part || ! optional_airtable_id_fields.has( field_name ) ) {

			parts.push( part );

		}

	}

	return parts.some( Boolean ) ? parts.join( '##' ) : '';

}

function clean_data_id_part( value ) {

	return clean_value( value )
		.replace( /[.%]/g, '' )
		.replace( /\s+/g, '-' );

}

function airtable_escape( value ) {

	return String( value || '' )
		.replaceAll( '\\', '\\\\' )
		.replaceAll( '\'', '\\\'' );

}

function build_field_ref( field_name ) {

	return `{${field_name}}`;

}

function build_contains_formula( field_name, value ) {

	const escaped_value = airtable_escape( String( value || '' ).toLowerCase() );

	return `FIND('${escaped_value}', LOWER(${build_field_ref( field_name )} & '')) > 0`;

}

function build_filter_formula( search_term ) {

	const trimmed_search_term = String( search_term || '' ).trim();

	if ( ! trimmed_search_term ) {

		return required_fields_filter;

	}

	const search_parts = airtable_fields.map( ( field_name ) => build_contains_formula( field_name, trimmed_search_term ) );

	return `AND(${required_fields_filter},OR(${search_parts.join( ',' )}))`;

}

function escape_html( value ) {

	return String( value )
		.replaceAll( '&', '&amp;' )
		.replaceAll( '<', '&lt;' )
		.replaceAll( '>', '&gt;' )
		.replaceAll( '"', '&quot;' )
		.replaceAll( '\'', '&#39;' );

}

function escape_attribute( value ) {

	return escape_html( value );

}

function extract_error_message( data ) {

	if ( typeof data?.error === 'string' && data.error.trim() ) {

		return data.error.trim();

	}

	if ( typeof data?.message === 'string' && data.message.trim() ) {

		return data.message.trim();

	}

	return 'De data kon niet geladen worden vanaf de Airtable proxy.';

}
